import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Button, Divider, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel
} from '@mui/material';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

// day: index into common:weekdaysShort (0 = Sunday)
const WEEK_DAYS: { value: WeekDay; day: 0 | 1 | 2 | 3 | 4 | 5 | 6 }[] = [
    { value: 'MONDAY', day: 1 },
    { value: 'TUESDAY', day: 2 },
    { value: 'WEDNESDAY', day: 3 },
    { value: 'THURSDAY', day: 4 },
    { value: 'FRIDAY', day: 5 },
    { value: 'SATURDAY', day: 6 },
    { value: 'SUNDAY', day: 0 },
];

const InstitutionSettingsPage: React.FC = () => {
    const { t } = useTranslation(['institutions', 'common']);
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [twoFactorPolicy, setTwoFactorPolicy] = useState<'OPTIONAL' | 'REQUIRED'>('OPTIONAL');
    const [savingTwoFactor, setSavingTwoFactor] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!institutionId) {
                setLoading(false);
                return;
            }
            try {
                const settings = await InstitutionSettingsService.getSettings(institutionId);
                if (settings?.weekendDays && settings.weekendDays.length > 0) {
                    setWeekendDays(settings.weekendDays);
                }
                if (settings?.twoFactorPolicy) {
                    setTwoFactorPolicy(settings.twoFactorPolicy);
                }
            } catch (error) {
                toast.showApiError(error, t('settingsPage.loadFailed'));
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleSave = async () => {
        if (!institutionId) {
            toast.showError(t('shared.noInstitutionSelected'));
            return;
        }
        if (weekendDays.length === 0) {
            toast.showError(t('shared.selectOneWeekendDay'));
            return;
        }
        if (weekendDays.length >= 7) {
            toast.showError(t('shared.weekendNotAllDays'));
            return;
        }
        try {
            setSaving(true);
            await InstitutionSettingsService.updateWeekendDays(institutionId, weekendDays);
            toast.showSuccess(t('shared.settingsSaved'));
        } catch (error) {
            toast.showApiError(error, t('shared.saveSettingsFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTwoFactorPolicy = async () => {
        if (!institutionId) {
            toast.showError(t('shared.noInstitutionSelected'));
            return;
        }
        try {
            setSavingTwoFactor(true);
            await InstitutionSettingsService.updateTwoFactorPolicy(institutionId, twoFactorPolicy);
            toast.showSuccess(t('settingsPage.twoFactorSaved'));
        } catch (error) {
            toast.showApiError(error, t('settingsPage.twoFactorSaveFailed'));
        } finally {
            setSavingTwoFactor(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><Typography>{t('shared.loading')}</Typography></Box>;
    }

    if (!institutionId) {
        return <Box sx={{ p: 3 }}><Typography>{t('shared.noInstitutionSelected')}</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>{t('settingsPage.title')}</Typography>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('settingsPage.weekendDays')}</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('shared.weekendHelp')}
                </Typography>

                <ToggleButtonGroup
                    value={weekendDays}
                    onChange={(_, next: WeekDay[]) => setWeekendDays(next)}
                    aria-label={t('shared.weekendDaysAria')}
                    size="small"
                    sx={{ mb: 3 }}
                >
                    {WEEK_DAYS.map(d => (
                        <ToggleButton key={d.value} value={d.value} aria-label={d.value}>
                            {t(`common:weekdaysShort.${d.day}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? t('shared.saving') : t('shared.saveSettings')}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('settingsPage.twoFactorTitle')}</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('settingsPage.twoFactorHelp')}
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={twoFactorPolicy === 'REQUIRED'}
                            onChange={(e) => setTwoFactorPolicy(e.target.checked ? 'REQUIRED' : 'OPTIONAL')}
                        />
                    }
                    label={t('settingsPage.requireTwoFactor')}
                    sx={{ mb: 2 }}
                />

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTwoFactorPolicy}
                        disabled={savingTwoFactor}
                    >
                        {savingTwoFactor ? t('shared.saving') : t('settingsPage.saveTwoFactor')}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default InstitutionSettingsPage;
